import { State as AuthorizationState } from './authorization/state';
import { BLOCKING_STATES, Model as Override } from './override/model';
import { Balance } from './payments/balance';

/** Where the claim's authorized money stands. */
export enum PaymentReadiness {
  /** Nothing to pay yet: not authorized, authorized for $0, or no approved job. */
  NOT_READY = 'NOT_READY',

  /** Authorized money is left for new payments. */
  READY = 'READY',

  /** Money is left, but an approval holds back the next payment. */
  HELD_BACK = 'HELD_BACK',

  /** All of it is on payments, but not every payment has completed. */
  IN_PROGRESS = 'IN_PROGRESS',

  /** Completed payments cover the authorized amount. */
  PAID = 'PAID',
}

/**
 * Same rule as the list filter, so rows and filters agree. Each active authorization (the first one and its
 * supplementals) has its own balance: ready while any has money left that nothing holds back.
 */
export function getPaymentReadiness(
  state: AuthorizationState | null | undefined,
  balances: Pick<Balance, 'authorizationId' | 'authorizedPayable' | 'paid' | 'remaining'>[] | null | undefined,
  isHeldBack: (authorizationId: string) => boolean = () => false
): PaymentReadiness {
  if (state !== AuthorizationState.PAYMENT_READY || !balances?.length) return PaymentReadiness.NOT_READY;

  const withMoneyLeft = balances.filter(balance => balance.paid < balance.authorizedPayable && balance.remaining > 0);
  if (withMoneyLeft.some(balance => !isHeldBack(balance.authorizationId))) return PaymentReadiness.READY;
  if (withMoneyLeft.length) return PaymentReadiness.HELD_BACK;

  return balances.every(balance => balance.paid >= balance.authorizedPayable)
    ? PaymentReadiness.PAID
    : PaymentReadiness.IN_PROGRESS;
}

/**
 * The approval holding back payments from the authorization, if any (same check as the payment gate).
 * A split is paid all or nothing: once one share is paid it can't change, so one share waiting holds back all.
 */
export function getPaymentHold<T extends Pick<Override, 'state' | 'targetId'>>(
  authorizationId: string,
  shareIds: string[],
  overrides: T[]
): T | null {
  const blocking = overrides.filter(override => BLOCKING_STATES.includes(override.state));
  const heldBy = (targetId: string) => blocking.find(override => override.targetId === targetId) ?? null;

  return heldBy(authorizationId) ?? shareIds.map(heldBy).find(Boolean) ?? null;
}

/** Extra claims list sorts; age uses the Opened column. */
export enum QueueSort {
  /** Earliest open hold due date; claims without one go last. */
  HOLD_DUE = 'Hold due',

  /** Earliest repair deadline first; claims without one go last. */
  REPAIR_DEADLINE = 'Repair deadline',
}

/** What needs attention on a claim, sent with each list row. */
export interface QueueSummary {
  openHolds: number;

  /** Earliest due date among the open evidence holds. */
  nextHoldDueDate: Date | null;

  openEscalations: number;

  /** Approvals still waiting for a manager. */
  pendingApprovals: number;

  paymentReadiness: PaymentReadiness;
}
